import React, { useCallback, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Platform,
  ActivityIndicator,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChildStackParamList } from "../../navigation/types";
import { useAuthStore } from "../../state/authStore";
import useKeydown from "../../hooks/useKeydown";
import { isFactorTv } from "@rnv/renative";
import { childrenApi, Child } from "../../api/children";
import { authApi } from "../../api/auth";
import { ApiError } from "../../api/client";
import { Mascot } from "../../components/Mascot";
import { QrCode } from "../../components/QrCode";
import KidModeLock from "../../components/KidModeLock";
import PinGate from "../../components/PinGate";
import { addChildUrl } from "../../web-url";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { colors, fontFamily } from "../../theme";

// Phone vs TV split: ProfileSelect doubles as the kid-mode entry on mobile,
// where the layout is much tighter than the TV's 1920×1080 canvas. Same
// detection rule the other shared screens use (ChildHomeScreen, etc.).
const SCREEN_W = Dimensions.get("window").width;
const SCREEN_H = Dimensions.get("window").height;
const IS_PHONE = Platform.OS !== "web" && SCREEN_W < 768;

type Props = NativeStackScreenProps<ChildStackParamList, "ProfileSelect">;

// Hard cap on how many kid profiles one parent can have. Three is enough
// to cover the typical family without letting a parent silently spawn
// dozens of accounts on a single subscription. Enforced in the UI by
// hiding the Add tile once the cap is reached; the backend should mirror
// this (see TODO in mapa-backend's children controller).
const MAX_CHILDREN = 3;

// Per-profile mascot color + bubble background. Index-based since the
// backend doesn't store color preferences yet.
const PROFILE_THEMES: { color: string; bg: string }[] = [
  { color: "#F18EB6", bg: "#FFE0EB" },
  { color: "#5B8DEF", bg: "#DCE7FF" },
  { color: "#7AC74F", bg: "#DEF1CF" },
  { color: "#FF8A4C", bg: "#FFE2C2" },
  { color: "#8C7DD9", bg: "#E6E0F7" },
];

// Self-heal helper: recover parentId from the JWT's `sub` claim when the
// auth store was persisted in an older format that didn't include it.
// Without this, the TV stays logged in but `useSessionSocket` bails on
// missing parentId and never streams events to the parent.
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const decoded =
      typeof atob === "function" ? atob(padded) : "";
    const payload = JSON.parse(decoded);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

export default function ProfileSelectScreen({ navigation }: Props) {
  const { accessToken, parentId, setParentId, setActiveChild, setKidMode, logout } =
    useAuthStore();
  const insets = useSafeAreaInsets();
  const [children, setChildren] = useState<Child[]>([]);
  // "Have we completed the first fetch?" — keeps the "Let's set up your family"
  // empty state from flashing before the children list arrives.
  const [loaded, setLoaded] = useState(false);
  const [focused, setFocused] = useState(0);
  // Brief banner shown when the kid presses OK on the dashed "Add on phone"
  // slot — TVs can't add children so we just tell them what to do instead.
  const [showAddHint, setShowAddHint] = useState(false);
  // Mobile-only PIN gate state for the "Add child" flow:
  //   pinStatus  — pre-fetched along with children so the Add tap is instant
  //   pinGateOpen — toggled when an existing PIN must be verified before
  //                 jumping to AddChild (no PIN → straight to PinSetup, see
  //                 startAddChildFlow below).
  const [pinStatus, setPinStatus] =
    useState<"unknown" | "has" | "none">("unknown");
  const [pinGateOpen, setPinGateOpen] = useState(false);

  // The Add tile is only rendered when there's room for another kid. Once
  // the parent hits MAX_CHILDREN, the picker is profiles-only. totalSlots
  // tracks the actual rendered count so the keypad clamps to the right
  // index range on TV (where the user navigates with arrow keys).
  const canAddMore = children.length < MAX_CHILDREN;
  const totalSlots = children.length + (canAddMore ? 1 : 0);
  const isAddSlotFocused =
    isFactorTv && canAddMore && focused === children.length;

  // Phone: size the profile circles so all tiles (at most 3 — kids, or kids +
  // the Add slot) sit on ONE centered row instead of wrapping 2-then-1. Stays
  // big with 1-2 profiles; shrinks just enough to fit 3 across the screen.
  const CIRCLE = IS_PHONE
    ? Math.max(
        72,
        Math.min(120, Math.floor((SCREEN_W - 40) / Math.max(totalSlots, 1)) - 36),
      )
    : 280;
  const MASCOT = IS_PHONE ? Math.round(CIRCLE * 0.8) : 240;
  const MASCOT_TOP = Math.round(CIRCLE * 0.15);

  // Self-heal stale auth: if we have a token but no parentId in the store
  // (older pairings persisted before parentId was added), decode the JWT to
  // recover it so WebSocket can connect.
  useEffect(() => {
    if (parentId || !accessToken) return;
    const sub = decodeJwtSub(accessToken);
    if (sub) setParentId(sub);
  }, [parentId, accessToken, setParentId]);

  // Loading/error state for the children fetch. On phone we surface this
  // inline; on TV we keep the original behavior of bouncing to Pairing
  // since a TV with no auth IS in a pre-pair state.
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadChildren = useCallback(() => {
    if (!accessToken) {
      // TV bounces to Pairing (it's the unpaired state). Phone shouldn't —
      // the RootNavigator's auth gate routes unauthenticated users to
      // Welcome, so reaching here without a token on phone is an edge case
      // we just render an error for.
      if (!IS_PHONE) navigation.replace("Pairing");
      else setLoadError("You're signed out. Please log in again.");
      return;
    }
    setLoadError(null);
    childrenApi
      .list(accessToken)
      .then((list) => {
        setChildren(list);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        // Expired/invalid JWT: clear the persisted auth and explicitly
        // route to Login (the Stack's initialRouteName won't re-evaluate
        // on its own). Both TV and phone hit this path.
        if (e instanceof ApiError && e.status === 401) {
          logout();
          if (!IS_PHONE) {
            navigation.replace("Pairing");
          } else {
            (navigation as any).replace("Login");
          }
          return;
        }
        if (!IS_PHONE) {
          navigation.replace("Pairing");
          return;
        }
        // Phone: keep the user on ProfileSelect with a clear inline error so
        // they're not silently teleported into the TV pairing UI.
        const msg = e instanceof Error ? e.message : String(e);
        setLoadError(`Couldn't load profiles. ${msg}`);
        setLoaded(true);
      });
  }, [accessToken, navigation, logout]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  // Re-fetch when the screen regains focus so a kid added via AddChild shows
  // up on return. Guarded on `addListener` because on TV this screen mounts
  // inside WebShell's plain nav object (no React Navigation), where
  // `useFocusEffect`/listeners don't exist — there the mount effect above is
  // enough since entering kid mode remounts the screen.
  useEffect(() => {
    const nav = navigation as any;
    if (typeof nav.addListener !== "function") return;
    const unsub = nav.addListener("focus", loadChildren);
    return unsub;
  }, [navigation, loadChildren]);

  // Prefetch PIN status so the "Add child" tap can branch instantly.
  // Failure → treat as no PIN so the flow proceeds (PinSetup will surface
  // any real backend issue when the parent tries to save).
  useEffect(() => {
    if (!IS_PHONE || !accessToken) return;
    let cancelled = false;
    authApi
      .pinStatus(accessToken)
      .then((r) => {
        if (!cancelled) setPinStatus(r.hasPin ? "has" : "none");
      })
      .catch(() => {
        if (!cancelled) setPinStatus("none");
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Mobile: "Add a child" tile. Replaces the old explanation Alert with a
  // direct flow — if a PIN is set, gate on it; if not, walk the parent
  // through PinSetup with AddChild as the post-save destination.
  const startAddChildFlow = useCallback(async () => {
    if (!accessToken) return;
    // Defensive: the Add tile is hidden at the cap, but a stale re-render
    // (or a future entry point) shouldn't bypass it.
    if (children.length >= MAX_CHILDREN) return;
    let status = pinStatus;
    if (status === "unknown") {
      try {
        const r = await authApi.pinStatus(accessToken);
        status = r.hasPin ? "has" : "none";
        setPinStatus(status);
      } catch {
        status = "none";
      }
    }
    if (status === "has") {
      setPinGateOpen(true);
      return;
    }
    // No PIN yet — leave kid mode and chain through PinSetup. After the
    // parent saves (or skips) the PIN, PinSetupScreen will land them on
    // AddChild thanks to `nextRoute`.
    setKidMode(false);
    (navigation as any).navigate("PinSetup", { nextRoute: "AddChild" });
  }, [accessToken, pinStatus, navigation, setKidMode, children.length]);

  // PinGate success → into parent mode + AddChild.
  const finishAddChildFlow = useCallback(() => {
    setPinGateOpen(false);
    setKidMode(false);
    (navigation as any).navigate("AddChild");
  }, [navigation, setKidMode]);

  useKeydown({
    isActive: true,
    // Clamp to the actual reachable range (children + 1 Add slot).
    left: () => setFocused((f) => Math.max(0, f - 1)),
    right: () =>
      setFocused((f) => Math.min(Math.max(totalSlots - 1, 0), f + 1)),
    ok: () => {
      if (focused < children.length) {
        const child = children[focused];
        if (child) {
          // Seed activeChild so ChildHome's greeting renders the kid's name
          // immediately, without waiting for its own /children round-trip.
          setActiveChild(child);
          // Flip into kid mode on selection. Without this, a parent who
          // entered ProfileSelect via the parent stack (e.g. after the
          // "Add child" flow) would land on ChildHome with kidMode still
          // false, hiding the KidModeLock badge that lets them get back
          // out. Selecting a kid is unambiguous "I'm in kid mode now."
          setKidMode(true);
          navigation.replace("ChildHome", { childId: child.id });
        }
      } else {
        // OK on the Add slot: open the QR overlay. Stays open until the
        // parent dismisses it with Back (no auto-timeout).
        setShowAddHint(true);
      }
    },
    // Back closes the QR overlay if it's open; otherwise it's a no-op here
    // (ProfileSelect is the TV's root kid screen).
    back: () => {
      if (showAddHint) setShowAddHint(false);
    },
  });

  return (
    <View
      style={[
        styles.root,
        {
          paddingTop: insets.top + (IS_PHONE ? 24 : 80),
          paddingBottom: insets.bottom + (IS_PHONE ? 24 : 80),
        },
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerMascotRing}>
          <View style={styles.headerMascotInner}>
            <Mascot
              mood="happy"
              color={colors.primary}
              size={IS_PHONE ? 44 : 88}
            />
          </View>
        </View>
        <Text style={styles.headerText}>Hi! Who's watching?</Text>
      </View>

      {/* Inline error banner — only shown on phone. Beats silently bouncing
          the user to the TV pairing screen when the children fetch fails. */}
      {loadError && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{loadError}</Text>
        </View>
      )}

      {/* Profile row — centered both axes. On phone it wraps (no horizontal
          scroll): 1-2 profiles sit centered on one line; 3 kids + the Add
          tile flow onto a second row instead of clipping or bounce-scrolling. */}
      <View style={styles.rowWrap}>
        {IS_PHONE && !loaded ? (
          <ActivityIndicator size="large" color={colors.primary} />
        ) : (
          <View style={[styles.row, IS_PHONE && styles.rowPhone]}>
          {children.map((child, i) => {
            const theme = PROFILE_THEMES[i % PROFILE_THEMES.length];
            const isFocused = isFactorTv && focused === i;
            return (
              <TouchableOpacity
                key={child.id}
                activeOpacity={0.85}
                onPress={() => {
                  setActiveChild(child);
                  // See `ok:` handler above — flipping kidMode on every
                  // profile selection keeps the lock badge visible no
                  // matter how the user arrived at ProfileSelect.
                  setKidMode(true);
                  navigation.replace("ChildHome", { childId: child.id });
                }}
                style={[
                  styles.slot,
                  { transform: [{ translateY: isFocused ? -6 : 0 }] },
                ]}
              >
                <View
                  style={[
                    styles.circleOuter,
                    isFocused && styles.circleOuterFocused,
                  ]}
                >
                  <View
                    style={[
                      styles.circle,
                      {
                        backgroundColor: theme.bg,
                        width: CIRCLE,
                        height: CIRCLE,
                        borderRadius: CIRCLE / 2,
                      },
                    ]}
                  >
                    <View
                      style={[styles.mascotInCircle, { marginTop: MASCOT_TOP }]}
                    >
                      <Mascot mood="happy" color={theme.color} size={MASCOT} />
                    </View>
                  </View>
                </View>
                <Text style={styles.name}>{child.name}</Text>
                <Text style={styles.age}>{child.age} years old</Text>
              </TouchableOpacity>
            );
          })}

          {/* Empty-state Add tile on phone — a single filled primary
              circle, no nested focus ring, no dashed border. The default
              Add slot below stacks `circleOuter` (focus ring) + dashed
              `addCircle` + solid-on-focus override; when there are 0 kids
              that nesting collapses into two concentric orange circles
              which read as a coffin shape on Android. The empty state has
              no arrow nav (it's the only tile), so we don't need the focus
              treatment anyway. TV keeps the original markup below. */}
          {!canAddMore ? null : IS_PHONE && children.length === 0 ? (
            <View style={styles.emptyWrap}>
              <Text style={styles.emptyHeadline}>Let's set up your family</Text>
              <Text style={styles.emptySubcopy}>
                Add your first kiddo to start watching together.
              </Text>
              <TouchableOpacity
                activeOpacity={0.85}
                onPress={startAddChildFlow}
                style={styles.emptyTile}
              >
                <View style={styles.emptyCircle}>
                  <View style={styles.emptyMascot}>
                    <Mascot mood="happy" color={colors.primary} size={104} />
                  </View>
                  <View style={styles.emptyBadge}>
                    <Text style={styles.emptyBadgePlus}>+</Text>
                  </View>
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={startAddChildFlow}
                style={styles.emptyCta}
              >
                <Text style={styles.emptyCtaText}>Add child</Text>
              </TouchableOpacity>
            </View>
          ) : (
            /* Add slot — focusable + acknowledges OK press.
               TV: pressing OK shows a QR overlay so the parent can scan
               with their phone (TVs can't add kids directly).
               Phone (with ≥1 kid present): drives the PIN-gated AddChild
               flow. */
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => {
                if (IS_PHONE) {
                  startAddChildFlow();
                } else {
                  setShowAddHint(true);
                }
              }}
              style={[
                styles.slot,
                // Phone users tap (no focus state to amplify), so keep the
                // Add tile fully visible alongside real profiles. TV keeps
                // the dim-until-focused behavior — that's how arrow nav
                // surfaces the current slot.
                { opacity: IS_PHONE ? 1 : isAddSlotFocused ? 1 : 0.5 },
                {
                  transform: [
                    { translateY: isAddSlotFocused ? -6 : 0 },
                  ],
                },
              ]}
            >
              <View
                style={[
                  styles.circleOuter,
                  // The add tile already shows its own ring (addCircle, which
                  // turns solid on focus) — keep the focus glow but drop this
                  // outer border so it reads as a single ring, not two.
                  isAddSlotFocused && styles.circleOuterFocused,
                  isAddSlotFocused && { borderColor: "transparent" },
                ]}
              >
                <View
                  style={[
                    styles.addCircle,
                    { width: CIRCLE, height: CIRCLE, borderRadius: CIRCLE / 2 },
                    // Mobile: always-solid orange border so the Add tile
                    // reads as an action, not a placeholder. TV keeps the
                    // dashed → solid-on-focus pattern.
                    IS_PHONE && {
                      borderColor: colors.primary,
                      borderStyle: "solid",
                    },
                    isAddSlotFocused && {
                      borderColor: colors.primary,
                      borderStyle: "solid",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.addPlus,
                      {
                        fontSize: Math.round(CIRCLE * 0.42),
                        lineHeight: Math.round(CIRCLE * 0.46),
                      },
                      // Mobile: primary-colored + so it pops; TV mirrors the
                      // text-color override the focus state already used.
                      (IS_PHONE || isAddSlotFocused) && {
                        color: colors.primary,
                      },
                    ]}
                  >
                    +
                  </Text>
                </View>
              </View>
              <Text
                style={[
                  styles.addLabel,
                  (IS_PHONE || isAddSlotFocused) && { color: colors.ink },
                ]}
              >
                {IS_PHONE ? "Add child" : "Add on phone"}
              </Text>
            </TouchableOpacity>
          )}
          </View>
        )}
      </View>

      {/* Footer hints. TV hints (remote arrow + hold-OK) don't apply on
          phone — instead point the parent at the lock badge that
          KidModeLock mounts in the top-right. */}
      {IS_PHONE ? (
        // When there are no kids, the empty state already shows its own
        // headline + subcopy + CTA — a redundant footer hint just adds noise.
        children.length > 0 ? (
          <View style={styles.footer}>
            <Text style={styles.hintText}>
              Tap a profile to start · Lock in the corner for parent mode
            </Text>
          </View>
        ) : null
      ) : (
        <View style={styles.footer}>
          <View style={styles.hintLeft}>
            <View style={styles.kbdBox}>
              <Text style={styles.kbdText}>↑↓</Text>
            </View>
            <Text style={styles.hintText}>
              Use remote arrows · OK to select
            </Text>
          </View>
          <Text style={styles.hintText}>
            Parent settings: hold · for 3 sec
          </Text>
        </View>
      )}

      {/* QR overlay shown when the kid presses OK on the Add slot. TVs
          can't add children directly — the parent scans the QR with their
          phone and lands on the Add-a-child page. Phone uses an Alert
          instead (handled in the Add slot's onPress above). */}
      {!IS_PHONE && showAddHint && (
        <TouchableOpacity
          style={styles.qrOverlay}
          activeOpacity={1}
          onPress={() => setShowAddHint(false)}
        >
          <View style={styles.qrCard}>
            <Text style={styles.qrTitle}>Add a child from your phone</Text>
            <Text style={styles.qrBody}>
              Scan with a parent's phone — you'll land right on the page.
            </Text>
            <View style={styles.qrFrame}>
              <QrCode value={addChildUrl()} size={280} />
            </View>
            <Text style={styles.qrFooter}>
              Press Back to close · or open Kidly on your phone manually.
            </Text>
          </View>
        </TouchableOpacity>
      )}

      {/* Parent-mode entry on mobile. Hidden until at least one child
          exists — on a fresh account there's no "kid mode" to exit and the
          lock badge + "No PIN set" warning are pointless. Once the parent
          adds a child, the lock surfaces normally on subsequent visits.
          Renders nothing on TV regardless (kidMode is false there). */}
      {children.length > 0 && <KidModeLock />}

      {/* PinGate for the "Add child" flow when a PIN is already set. On
          success we flip out of kid mode and navigate to AddChild. */}
      {pinGateOpen && (
        <PinGate
          onSuccess={finishAddChildFlow}
          onCancel={() => setPinGateOpen(false)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
    paddingHorizontal: IS_PHONE ? 20 : 100,
    paddingVertical: IS_PHONE ? 24 : 80,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerMascotRing: {
    width: IS_PHONE ? 52 : 96,
    height: IS_PHONE ? 52 : 96,
    borderRadius: IS_PHONE ? 26 : 48,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginRight: IS_PHONE ? 12 : 20, // gap replacement (Chromium <84)
  },
  headerMascotInner: {
    marginTop: IS_PHONE ? 6 : 14,
  },
  headerText: {
    fontFamily,
    fontSize: IS_PHONE ? 22 : 36,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 0.4,
    flexShrink: 1,
  },
  // Wrapper that fills the middle of the screen so the row centers on
  // both axes regardless of profile count.
  rowWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  // ScrollView contentContainer: keeps the row centered when it fits, and
  // pads the edges so the first/last slot doesn't sit against the screen.
  rowContent: {
    paddingHorizontal: IS_PHONE ? 4 : 0,
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  // Phone: wrap so 3 kids + the Add tile flow onto a second row instead of
  // clipping or needing a horizontal scroll.
  rowPhone: {
    flexWrap: "wrap",
  },
  slot: {
    alignItems: "center",
    padding: IS_PHONE ? 8 : 12,
    // gap replacement — every slot gets right margin; the last one's
    // trailing margin is invisible thanks to justifyContent: center on `row`.
    marginRight: IS_PHONE ? 8 : 80,
    // Vertical gap if slots ever wrap onto a second row on a very narrow phone
    // (normally they don't — circles are sized to fit one row).
    marginBottom: IS_PHONE ? 8 : 0,
  },
  circleOuter: {
    padding: IS_PHONE ? 4 : 8,
    borderRadius: 999,
    borderWidth: IS_PHONE ? 4 : 8,
    borderColor: "transparent",
    // gap replacement — vertical spacing to the name below
    marginBottom: IS_PHONE ? 10 : 22,
  },
  circleOuterFocused: {
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 22 },
    shadowOpacity: 0.28,
    shadowRadius: 44,
    elevation: 12,
  },
  circle: {
    width: IS_PHONE ? 120 : 280,
    height: IS_PHONE ? 120 : 280,
    borderRadius: IS_PHONE ? 60 : 140,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  mascotInCircle: {
    marginTop: IS_PHONE ? 18 : 44,
  },
  name: {
    fontFamily,
    fontSize: IS_PHONE ? 22 : 44,
    fontWeight: "900",
    color: colors.ink,
    marginBottom: IS_PHONE ? 4 : 22, // gap replacement
  },
  age: {
    fontFamily,
    fontSize: IS_PHONE ? 14 : 22,
    fontWeight: "700",
    color: colors.ink2,
    marginTop: IS_PHONE ? -2 : -10,
  },
  addCircle: {
    width: IS_PHONE ? 120 : 280,
    height: IS_PHONE ? 120 : 280,
    borderRadius: IS_PHONE ? 60 : 140,
    borderWidth: IS_PHONE ? 3 : 5,
    borderStyle: "dashed",
    borderColor: colors.lineStrong,
    alignItems: "center",
    justifyContent: "center",
  },
  // Phone empty-state group: a friendly headline + Mascot circle + pill
  // CTA. Mirrors the look-and-feel of the populated profile picker (soft
  // pastel circle + Mascot) so the screen doesn't suddenly become a
  // hard-edged orange button when the family is empty.
  emptyWrap: {
    alignItems: "center",
    paddingHorizontal: 24,
  },
  emptyHeadline: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    textAlign: "center",
    marginBottom: 8,
  },
  emptySubcopy: {
    fontFamily,
    fontSize: 14,
    fontWeight: "600",
    color: colors.ink3,
    textAlign: "center",
    maxWidth: 280,
    marginBottom: 24,
  },
  emptyTile: {
    alignItems: "center",
    marginBottom: 24,
  },
  emptyCircle: {
    width: 168,
    height: 168,
    borderRadius: 84,
    // Soft warm background — same family as PROFILE_THEMES[3] but a touch
    // lighter so the Mascot reads well at scale.
    backgroundColor: "#FFE2C2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
  },
  emptyMascot: {
    // Mascot artwork sits below its viewBox center — nudge down a little
    // so the smile lands in the visual center of the circle.
    marginTop: 20,
  },
  emptyBadge: {
    position: "absolute",
    right: 6,
    bottom: 6,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 4,
    borderColor: colors.bg,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 8,
    elevation: 6,
  },
  emptyBadgePlus: {
    fontFamily,
    fontSize: 26,
    lineHeight: 30,
    fontWeight: "400",
    color: "#fff",
    // Optical centering — RN's text baseline puts "+" slightly low.
    marginTop: -2,
  },
  emptyCta: {
    backgroundColor: colors.primary,
    paddingHorizontal: 36,
    paddingVertical: 14,
    borderRadius: 999,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 14,
    elevation: 5,
  },
  emptyCtaText: {
    fontFamily,
    fontSize: 16,
    fontWeight: "800",
    color: "#fff",
    letterSpacing: 0.3,
  },
  addPlus: {
    fontFamily,
    fontSize: IS_PHONE ? 48 : 92,
    fontWeight: "300",
    color: colors.ink3,
    lineHeight: IS_PHONE ? 52 : 100,
  },
  addLabel: {
    fontFamily,
    fontSize: IS_PHONE ? 14 : 26,
    fontWeight: "800",
    color: colors.ink3,
  },
  footer: {
    flexDirection: "row",
    justifyContent: IS_PHONE ? "center" : "space-between",
    alignItems: "center",
  },
  errorBanner: {
    marginTop: 12,
    marginHorizontal: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: "#FFE2C2",
    borderWidth: 1,
    borderColor: "#FF8A4C",
  },
  errorText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: "#B45A24",
  },
  hintLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  kbdBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: colors.ink,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14, // gap replacement
  },
  kbdText: {
    fontFamily,
    color: "#fff",
    fontSize: 16,
    fontWeight: "800",
  },
  hintText: {
    fontFamily,
    fontSize: IS_PHONE ? 13 : 20,
    fontWeight: "700",
    color: colors.ink3,
    textAlign: "center",
  },

  // QR overlay for "Add on phone"
  qrOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    // Explicit viewport dimensions instead of right/bottom:0. On Tizen/webOS
    // (Chromium 76) the flex chain from #root collapses the parent height, so
    // bottom:0 only reached ~half the screen — locking to Dimensions fills it.
    width: SCREEN_W,
    height: SCREEN_H,
    backgroundColor: "rgba(46,35,22,0.55)",
    alignItems: "center",
    justifyContent: "center",
  },
  qrCard: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 56,
    paddingVertical: 44,
    borderRadius: 28,
    alignItems: "center",
    maxWidth: 640,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 22 },
    shadowOpacity: 0.32,
    shadowRadius: 44,
    elevation: 18,
  },
  qrTitle: {
    fontFamily,
    fontSize: 36,
    fontWeight: "900",
    color: colors.ink,
    textAlign: "center",
    marginBottom: 8,
  },
  qrBody: {
    fontFamily,
    fontSize: 20,
    fontWeight: "600",
    color: colors.ink2,
    textAlign: "center",
    maxWidth: 460,
    marginBottom: 28,
  },
  qrFrame: {
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 4,
    borderColor: colors.ink,
    marginBottom: 20,
  },
  qrFooter: {
    fontFamily,
    fontSize: 16,
    fontWeight: "600",
    color: colors.ink3,
    textAlign: "center",
  },
});
